import React, { useEffect, useState } from 'react';
import { Card } from '../../ui/Card';
import { Spinner } from '../../ui/Spinner';
import { apiClient } from '../../../lib/api';
import { useToast } from '../../../hooks/useToast';
import { NotificationPreference, NOTIFICATION_CATEGORIES } from '../../../types';

interface ToggleSwitchProps {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  ariaLabel?: string;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  id,
  checked,
  onChange,
  disabled = false,
  ariaLabel,
}) => {
  return (
    <div
      id={id}
      role="switch"
      tabIndex={disabled ? -1 : 0}
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => !disabled && onChange(!checked)}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onChange(!checked);
        }
      }}
      className={`
        relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full
        border-2 border-transparent transition-colors duration-200 ease-in-out
        focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        ${checked ? 'bg-primary-600' : 'bg-neutral-200'}
      `}
    >
      <span
        className={`
          pointer-events-none inline-block h-5 w-5 transform rounded-full
          bg-white shadow ring-0 transition duration-200 ease-in-out
          ${checked ? 'translate-x-5' : 'translate-x-0'}
        `}
      />
    </div>
  );
};

export const NotificationsSection: React.FC = () => {
  const { showToast } = useToast();
  const [preferences, setPreferences] = useState<NotificationPreference[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState<string | null>(null);

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.getNotificationPreferences();
      setPreferences(response.categories);
    } catch (error) {
      console.error('Failed to load notification preferences:', error);
      showToast('Failed to load notification preferences', 'error');
      // Set defaults from constants if API fails
      setPreferences(
        NOTIFICATION_CATEGORIES.map((cat) => ({
          ...cat,
          enabled: true,
        }))
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = async (category: string, enabled: boolean) => {
    setIsSaving(category);

    // Optimistically update UI
    setPreferences((prev) =>
      prev.map((pref) =>
        pref.category === category ? { ...pref, enabled } : pref
      )
    );

    try {
      const response = await apiClient.updateNotificationPreferences([
        { category, enabled },
      ]);
      // Update with server response
      setPreferences(response.categories);
      showToast(
        enabled ? 'Notifications enabled' : 'Notifications disabled',
        'success'
      );
    } catch (error) {
      console.error('Failed to update notification preference:', error);
      showToast('Failed to update preference', 'error');
      // Revert on error
      setPreferences((prev) =>
        prev.map((pref) =>
          pref.category === category ? { ...pref, enabled: !enabled } : pref
        )
      );
    } finally {
      setIsSaving(null);
    }
  };

  if (isLoading) {
    return <Spinner size="md" text="Loading preferences..." center />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-[16px] font-semibold text-neutral-900 mb-1">
          Email Notifications
        </h3>
        <p className="text-[13px] text-neutral-500">
          Choose which emails you'd like to receive.
        </p>
      </div>

      <Card padding="none" className="divide-y divide-neutral-100">
        {preferences.map((pref) => (
          <div
            key={pref.category}
            className="flex items-center justify-between px-5 py-4"
          >
            <div className="flex-1 pr-4">
              <label
                htmlFor={`toggle-${pref.category}`}
                className="block text-[14px] font-medium text-neutral-900 cursor-pointer"
              >
                {pref.label}
              </label>
              <p
                id={`${pref.category}-description`}
                className="text-[13px] text-neutral-500 mt-0.5"
              >
                {pref.description}
              </p>
            </div>
            <ToggleSwitch
              id={`toggle-${pref.category}`}
              checked={pref.enabled}
              onChange={(enabled) => handleToggle(pref.category, enabled)}
              disabled={isSaving === pref.category}
              ariaLabel={`Toggle ${pref.label} notifications`}
            />
          </div>
        ))}
      </Card>
    </div>
  );
};
